import { SyntheticEvent, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import PeopleAltOutlined from '@mui/icons-material/PeopleAltOutlined';
import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined';
import { useParams } from 'react-router-dom';
import { Loader, theme } from '@omnigenbiodata/ui';

import PortalLayout from '../../../../../../components/layouts/PortalLayout';
import { useAppDispatch, useAppSelector } from '../../../../../../store';
import { getParticipantThunk, isBusySelector, responseSelector } from '../../../../../../store/participant';
import { EhrStatus, OrderStatus, ParticipantStatus } from '../../../../../../core/types/common.types';

import ParticipantSummary from './components/ParticipantSummary';
import ParticipantFeature from './components/ParticipantFeature';
import ParticipantProfile from './components/ParticipantProfile';
import ParticipantTimeline from './components/ParticipantTimeline';
import ParticipantSite from './components/ParticipantSite';
import ParticipantEHR from './components/ParticipantEHR';
import ParticipantConsent from './components/ParticipantConsent';
import ParticipantSample from './components/ParticipantSample';
import { ParticipantAction } from './components/ParticipantActionMenu';
import ParticipantCompliance from './components/ParticipantCompliance';
import ParticipantActionDialog from './components/ParticipantActionDialog';
import PageBanner from '../../../../../../components/layouts/PageBanner';

const primaryTint = theme.palette.grey[100];
const primaryTintStrong = theme.palette.primary.lighter;
const successTint = theme.palette.success.lighter;

function hasParticipantError(status: ParticipantStatus | string | null | undefined) {
  return (
    status === ParticipantStatus.WITHDRAWN ||
    status === ParticipantStatus.DECEASED ||
    status === ParticipantStatus.SUSPENDED
  );
}

function isParticipantActive(status: ParticipantStatus | string | null | undefined) {
  return status === ParticipantStatus.ACTIVE;
}

function hasSuccessfulEhr(ehrStatus: EhrStatus | string | null | undefined) {
  return ehrStatus === EhrStatus.SUCCESS;
}

function hasSuccessfulSample(orderStatus: OrderStatus | string | null | undefined) {
  return (
    orderStatus === OrderStatus.RECEIVED || orderStatus === OrderStatus.STORED || orderStatus === OrderStatus.PROCESSED
  );
}

function hasAncestryReleased(orderStatus: OrderStatus | string | null | undefined) {
  return orderStatus === OrderStatus.PROCESSED;
}

function ParticipantScene() {
  const [formAction, setFormAction] = useState<ParticipantAction | null>(null);
  const [selectedTab, setSelectedTab] = useState('tab-enrolment');
  const [isDialogOpen, setDialogOpen] = useState(false);

  const dispatch = useAppDispatch();

  const { participantId } = useParams<{
    participantId: string;
  }>();

  const isBusy = useAppSelector(isBusySelector);
  const response = useAppSelector(responseSelector);

  useEffect(() => {
    if (participantId) {
      dispatch(getParticipantThunk(participantId));
    }
  }, [dispatch, participantId]);

  const handleTabChange = (_event: SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const handleActionOpen = (action: ParticipantAction) => {
    setFormAction(action);
    setDialogOpen(true);
  };

  const handleActionClose = () => {
    setDialogOpen(false);
  };

  return (
    <PortalLayout>
      <Loader isVisible={isBusy} label="Loading Participant" />

      {response && !isBusy && (
        <Box pb={3}>
          <ParticipantActionDialog
            isOpen={isDialogOpen}
            onClose={handleActionClose}
            formAction={formAction}
            participant={response}
          />
          <PageBanner
            title="Participant Record"
            description="Review participant enrolment, consent, site, EHR, sample and compliance information."
            status={hasParticipantError(response.participantStatus) ? 'error' : undefined}
            extras={
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'rgba(16, 53, 68, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: hasParticipantError(response.participantStatus) ? 'error.lighter' : successTint,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                  aria-hidden="true"
                >
                  <PeopleAltOutlined
                    color={hasParticipantError(response.participantStatus) ? 'error' : 'primary'}
                    sx={{ fontSize: 28 }}
                  />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="overline" component="p" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                    Current record
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
                    <Chip
                      label={response.participantRef || 'No reference'}
                      size="small"
                      sx={{
                        bgcolor: primaryTintStrong,
                        color: 'primary.main',
                        fontWeight: 700,
                      }}
                    />

                    <Chip
                      label={response.participantStatus || 'UNKNOWN'}
                      size="small"
                      color={hasParticipantError(response.participantStatus) ? 'error' : 'success'}
                      sx={{
                        fontWeight: 700,
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            }
          />

          <Paper
            elevation={4}
            sx={{
              border: 1,
              borderColor: 'rgba(16, 53, 68, 0.18)',
              bgcolor: 'background.paper',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: 6,
                bgcolor: hasParticipantError(response.participantStatus) ? 'error.main' : 'primary.main',
              }}
            />

            <Box p={{ xs: 2, md: 4 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md>
                  <ParticipantSummary
                    firstName={response.firstName}
                    surname={response.surname}
                    status={response.participantStatus}
                    participantRef={response.participantRef}
                    hasError={hasParticipantError(response.participantStatus)}
                    onAction={handleActionOpen}
                  />
                </Grid>

                <Grid item xs={6} sm={3} md={1}>
                  <ParticipantFeature
                    label="Status"
                    enabled={isParticipantActive(response.participantStatus)}
                    enabledText="Active"
                    disabledText={response.participantStatus}
                    disabledBorderColor="error.light"
                    disabledColor="error.lighter"
                    disabledIconColor="error"
                  />
                </Grid>

                <Grid item xs={6} sm={3} md={1}>
                  <ParticipantFeature
                    label="EHR"
                    enabled={hasSuccessfulEhr(response.ehr.ehrStatus)}
                    enabledText="Received"
                    disabledText="Not received"
                  />
                </Grid>

                <Grid item xs={6} sm={3} md={1}>
                  <ParticipantFeature
                    label="Sample"
                    enabled={hasSuccessfulSample(response.sample.orderStatus)}
                    enabledText="Received"
                    disabledText="Pending"
                  />
                </Grid>

                <Grid item xs={6} sm={3} md={1}>
                  <ParticipantFeature
                    label="Ancestry"
                    enabled={hasAncestryReleased(response.sample.orderStatus)}
                    enabledText="Released"
                    disabledText="Not released"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3} mt={1}>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      border: 1,
                      borderColor: 'rgba(16, 53, 68, 0.12)',
                      bgcolor: 'background.paper',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        pt: 1,
                        bgcolor: primaryTint,
                        borderBottom: 1,
                        borderColor: 'rgba(16, 53, 68, 0.12)',
                      }}
                    >
                      <TabContext value={selectedTab}>
                        <TabList
                          onChange={handleTabChange}
                          aria-label="Participant details tabs"
                          variant="scrollable"
                          scrollButtons="auto"
                          sx={{
                            minHeight: 48,
                            '& .MuiTab-root': {
                              fontWeight: 700,
                              color: 'text.secondary',
                              minHeight: 48,
                              textTransform: 'none',
                            },
                            '& .Mui-selected': {
                              color: 'primary.main',
                            },
                            '& .MuiTabs-indicator': {
                              height: 3,
                              bgcolor: 'success.main',
                              borderRadius: 999,
                            },
                          }}
                        >
                          <Tab label="Enrolment" value="tab-enrolment" id="tab-enrolment" />
                          <Tab label="Participant" value="tab-participant" id="tab-participant" />
                          <Tab label="Site" value="tab-site" id="tab-site" />
                          <Tab label="EHR" value="tab-ehr" id="tab-ehr" />
                          <Tab label="Sample" value="tab-sample" id="tab-sample" />
                          <Tab label="Compliance" value="tab-compliance" id="tab-compliance" />
                        </TabList>

                        <Box sx={{ p: 2 }}>
                          <TabPanel value="tab-enrolment" id="tab-enrolment-panel" sx={{ p: 0 }}>
                            <ParticipantConsent {...response} />
                          </TabPanel>

                          <TabPanel value="tab-participant" id="tab-participant-panel" sx={{ p: 0 }}>
                            <ParticipantProfile
                              participantStatus={response.participantStatus}
                              firstName={response.firstName}
                              surname={response.surname}
                              nhsNumber={response.nhsNumber}
                              sexAtBirth={response.sexAtBirth}
                              genderIdentity={response.genderIdentity}
                              dob={response.dob}
                              email={response.email}
                              mobile={response.mobile}
                              landline={response.landline}
                              address1={response.address1}
                              address2={response.address2}
                              town={response.town}
                              postcode={response.postcode}
                            />
                          </TabPanel>

                          <TabPanel value="tab-site" id="tab-site-panel" sx={{ p: 0 }}>
                            <ParticipantSite {...response.site} />
                          </TabPanel>

                          <TabPanel value="tab-ehr" id="tab-ehr-panel" sx={{ p: 0 }}>
                            <ParticipantEHR response={response} />
                          </TabPanel>

                          <TabPanel value="tab-sample" id="tab-sample-panel" sx={{ p: 0 }}>
                            <ParticipantSample {...response.sample} />
                          </TabPanel>

                          <TabPanel value="tab-compliance" id="tab-compliance-panel" sx={{ p: 0 }}>
                            <ParticipantCompliance participant={response} />
                          </TabPanel>
                        </Box>
                      </TabContext>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: '100%',
                      border: 1,
                      borderColor: 'rgba(16, 53, 68, 0.12)',
                      bgcolor: 'background.paper',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        px: 2.5,
                        py: 2,
                        bgcolor: primaryTint,
                        borderBottom: 1,
                        borderColor: 'rgba(16, 53, 68, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: 'background.paper',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                        aria-hidden="true"
                      >
                        <AssignmentOutlined color="primary" sx={{ fontSize: 26 }} />
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" component="h2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          Participant timeline
                        </Typography>

                        <Typography variant="body2" component="p" color="text.secondary">
                          Key events and status changes for this participant.
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ p: 2 }}>
                      <ParticipantTimeline participant={response} />
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>
      )}
    </PortalLayout>
  );
}

export default ParticipantScene;
